// Libs
import clsx from "clsx";
import React from "react";

// Utils
import { type RTableCellProps, wrapKeyUp, wrapPrevent } from "../../core/utils";

// Components
import type { TableBorderMode } from "src/design-system/organisms/table";
import { IconWrapper, type IconWrapperProps } from "../icons";

export type SortDirection = "asc" | "desc";

export interface HeaderCellProps extends React.PropsWithChildren<RTableCellProps> {
    /**
     * Align content
     */
    align?: "left" | "center" | "right";
    /**
     * Additional className
     */
    className?: string;
    /**
     * If has sort
     */
    hasSort?: boolean;
    /**
     * If sort is active
     */
    isSortActive?: boolean;
    /**
     * Click handler
     */
    onSort?: (direction: SortDirection) => void | Promise<void>;
    /**
     * Additional root className
     */
    rootClassName?: string;
    /**
     * Sort direction
     */
    sortDirection?: SortDirection;
    /**
     * check if the first left column static
     */
    isStatic?: boolean;
    /**
     * border mode for header cell
     */
    borderMode?: TableBorderMode;
    /**
     * apply right border (used for grouped border mode)
     */
    hasRightBorder?: boolean;
}

const SORT_OPPOSITES: Record<SortDirection, SortDirection> = {
    asc: "desc",
    desc: "asc",
};

export const HeaderCell = React.memo(function HeaderCell({
    align = "left",
    children,
    className,
    hasSort = false,
    isSortActive = false,
    onSort,
    rootClassName: rootClassNameProp,
    sortDirection = "asc",
    isStatic = false,
    borderMode = "none",
    hasRightBorder = false,
    ...otherProps
}: HeaderCellProps) {
    const rootClassName = clsx(
        "ds-c-header-cell",
        {
            "ds-c-header-cell--bordered": borderMode === "all",
            "ds-c-header-cell--bordered-right": borderMode === "group" && hasRightBorder,
            "ds-c-header-cell--sorted": hasSort && !!sortDirection && isSortActive,
            "ds-c-header-cell--static": isStatic,
        },
        `ds-c-header-cell--${align}`,
        rootClassNameProp
    );

    const containerClassName = clsx(
        { "ds-o-actionable": hasSort },
        "ds-c-header-cell__container",
        "ds-u-typography--caption-medium",
        "ds-u-text--uppercase",
        className
    );

    const iconClassName = clsx("ds-c-header-cell__icon");

    const handleClick = wrapPrevent(
        () => onSort(SORT_OPPOSITES[sortDirection ?? "desc"]),
        !hasSort
    );
    const handleKeyUp = wrapKeyUp(handleClick);

    return (
        <th
            {...otherProps}
            className={rootClassName}
            onClick={handleClick}
            onKeyUp={handleKeyUp}
            role="button"
            tabIndex={onSort ? 0 : -1}
        >
            <div className={containerClassName}>
                {hasSort ? (
                    <SortIcon
                        iconClassName={iconClassName}
                        size="s"
                        position={["left", "center"].includes(align) ? "right" : "left"}
                        isSortActive={isSortActive}
                        sortDirection={sortDirection}
                        fillColorTuple={["var(--ds-color--grey-300)", "var(--ds-color--grey-100)"]}
                    >
                        <div className="ds-o-flex-item--1 ds-c-header-cell__content">
                            {children}
                        </div>
                    </SortIcon>
                ) : (
                    <div className="ds-o-flex-item--1 ds-c-header-cell__content">{children}</div>
                )}
            </div>
        </th>
    );
});

interface SortIconProps extends Omit<IconWrapperProps, "icon"> {
    isSortActive: boolean;
    sortDirection: SortDirection;
    fillColorTuple: [string, string];
}

function SortIcon({
    children,
    isSortActive,
    sortDirection,
    fillColorTuple,
    ...otherProps
}: SortIconProps) {
    const sortCondition = !sortDirection || sortDirection === "asc";
    const fillColors = sortCondition ? fillColorTuple : [fillColorTuple[1], fillColorTuple[0]];

    return (
        <IconWrapper
            icon="isSort"
            iconProps={isSortActive ? { fillColorList: fillColors } : undefined}
            {...otherProps}
        >
            {children}
        </IconWrapper>
    );
}
